// SPDX-License-Identifier: AGPL-3.0-or-later

import {EmailVerificationAlert} from '@app/features/app/components/dialogs/components/EmailVerificationAlert';
import {UnclaimedAccountAlert} from '@app/features/app/components/dialogs/components/UnclaimedAccountAlert';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {openClaimAccountModal} from '@app/features/auth/components/modals/ClaimAccountModal';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {EnvelopeSimpleIcon, LockSimpleIcon} from '@app/features/ui/icons/Icons';
import {EmailChangeModal} from '@app/features/user/components/modals/EmailChangeModal';
import {PasswordChangeModal} from '@app/features/user/components/modals/PasswordChangeModal';
import styles from '@app/features/user/components/modals/tabs/account_security_tab/AccountTab.module.css';
import type {User} from '@app/features/user/models/User';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const HIDE_EMAIL_TOGGLE_DESCRIPTOR = msg({
	message: 'Hide',
	comment: 'Account settings: button that hides the user email address (currently revealed). Verb, sentence case.',
});
const REVEAL_EMAIL_TOGGLE_DESCRIPTOR = msg({
	message: 'Reveal',
	comment: 'Account settings: button that reveals the masked user email address. Verb, sentence case.',
});
const maskEmail = (email: string): string => {
	const [username, domain] = email.split('@');
	const maskedUsername = username.replace(/./g, '*');
	return `${maskedUsername}@${domain}`;
};

interface AccountTabProps {
	user: User;
	isClaimed: boolean;
	showMaskedEmail: boolean;
	setShowMaskedEmail: (show: boolean) => void;
}

export const AccountTabContent: React.FC<AccountTabProps> = observer(
	({user, isClaimed, showMaskedEmail, setShowMaskedEmail}) => {
		const {i18n} = useLingui();
		const openEmailChange = () =>
			ModalCommands.push(
				modal(() => (
					<EmailChangeModal
						user={user}
						data-flx="user.account-security-tab.account-tab.account-tab-content.email-change-modal"
					/>
				)),
			);
		const emailRow = isClaimed ? (
			<SettingsRow
				icon={<EnvelopeSimpleIcon size={20} />}
				title={<Trans>Email address</Trans>}
				description={
					<span
						className={styles.emailRow}
						data-flx="user.account-security-tab.account-tab.account-tab-content.email-value-row"
					>
						<span
							className={`${styles.emailText} ${showMaskedEmail ? styles.emailTextSelectable : ''}`}
							data-flx="user.account-security-tab.account-tab.account-tab-content.email-text"
						>
							{showMaskedEmail ? user.email : maskEmail(user.email!)}
						</span>
						<button
							type="button"
							className={styles.toggleButton}
							onClick={() => setShowMaskedEmail(!showMaskedEmail)}
							data-flx="user.account-security-tab.account-tab.account-tab-content.toggle-email-visibility"
						>
							{showMaskedEmail ? i18n._(HIDE_EMAIL_TOGGLE_DESCRIPTOR) : i18n._(REVEAL_EMAIL_TOGGLE_DESCRIPTOR)}
						</button>
					</span>
				}
				control={() =>
					RuntimeConfig.emailsEnabled ? (
						<Button
							variant="secondary"
							small={true}
							onClick={openEmailChange}
							data-flx="user.account-security-tab.account-tab.account-tab-content.change-email-button"
						>
							<Trans>Change email</Trans>
						</Button>
					) : null
				}
				data-flx="user.account-security-tab.account-tab.account-tab-content.email-row"
			/>
		) : (
			<SettingsRow
				icon={<EnvelopeSimpleIcon size={20} />}
				title={<Trans>Email address</Trans>}
				description={
					<span
						className={styles.warningText}
						data-flx="user.account-security-tab.account-tab.account-tab-content.email-warning"
					>
						<Trans>No email address set</Trans>
					</span>
				}
				control={() => (
					<Button
						variant="secondary"
						small={true}
						fitContent
						onClick={() => openClaimAccountModal()}
						data-flx="user.account-security-tab.account-tab.account-tab-content.add-email-button"
					>
						<Trans>Add email</Trans>
					</Button>
				)}
				data-flx="user.account-security-tab.account-tab.account-tab-content.email-row"
			/>
		);
		const passwordRow = isClaimed ? (
			<SettingsRow
				icon={<LockSimpleIcon size={20} />}
				title={<Trans>Password</Trans>}
				description={
					user.passwordLastChangedAt ? (
						<Trans>Last changed: {DateUtils.getRelativeDateString(user.passwordLastChangedAt, i18n)}</Trans>
					) : (
						<Trans>Last changed: never</Trans>
					)
				}
				control={() => (
					<Button
						variant="secondary"
						small={true}
						onClick={() =>
							ModalCommands.push(
								modal(() => (
									<PasswordChangeModal data-flx="user.account-security-tab.account-tab.account-tab-content.password-change-modal" />
								)),
							)
						}
						data-flx="user.account-security-tab.account-tab.account-tab-content.change-password-button"
					>
						<Trans>Change password</Trans>
					</Button>
				)}
				data-flx="user.account-security-tab.account-tab.account-tab-content.password-row"
			/>
		) : (
			<SettingsRow
				icon={<LockSimpleIcon size={20} />}
				title={<Trans>Password</Trans>}
				description={
					<span
						className={styles.warningText}
						data-flx="user.account-security-tab.account-tab.account-tab-content.password-warning"
					>
						<Trans>No password set</Trans>
					</span>
				}
				control={() => (
					<Button
						variant="secondary"
						small={true}
						fitContent
						onClick={() => openClaimAccountModal()}
						data-flx="user.account-security-tab.account-tab.account-tab-content.set-password-button"
					>
						<Trans>Set password</Trans>
					</Button>
				)}
				data-flx="user.account-security-tab.account-tab.account-tab-content.password-row"
			/>
		);
		return (
			<>
				{!isClaimed && (
					<UnclaimedAccountAlert data-flx="user.account-security-tab.account-tab.account-tab-content.unclaimed-account-alert" />
				)}
				<div className={styles.accountRows} data-flx="user.account-security-tab.account-tab.account-tab-content.rows">
					{emailRow}
					{passwordRow}
				</div>
				{isClaimed && RuntimeConfig.emailsEnabled && user.email && !user.verified && (
					<EmailVerificationAlert data-flx="user.account-security-tab.account-tab.account-tab-content.email-verification-alert" />
				)}
			</>
		);
	},
);
